import React, { useState, useEffect, useRef } from 'react';

import { createPortal } from 'react-dom';
import { MdNote, MdEdit, MdLaunch } from 'react-icons/md';
import { BsPauseBtnFill, BsFillPlayBtnFill, BsPip } from 'react-icons/bs';
import { FaVolumeUp, FaVolumeMute, FaTrophy, FaBell, FaRegStopCircle } from 'react-icons/fa';
import { RiDeleteBin4Fill } from 'react-icons/ri';
import { HiClock, HiXMark } from 'react-icons/hi2';

import { IoClose, IoExpand } from 'react-icons/io5';
import { SessionData, FocusSession } from './types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import useScrollLock from '../../../hooks/useScrollLock';
import CheckIns from './CheckIns';
import { ToastContainer } from 'react-toastify';
import { FaCircleStop } from 'react-icons/fa6';

const ScreenSharePreview: React.FC<{ streamRef: React.RefObject<MediaStream | null> }> = ({ streamRef }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const stream = streamRef?.current;
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
    return () => {
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [streamRef]);
  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      className="w-full max-h-64 object-contain bg-black"
    />
  );
};

interface FocusSessionModalProps {
  open: boolean;
  activeMode: 'solo' | 'team';
  sessionData: SessionData;
  currentSession: FocusSession | null;
  sessionTimeLeft: number;
  sessionStarted: boolean;
  sessionPaused: boolean;
  sessionCompleted: boolean;
  sessionTab: 'teams' | 'notes' | 'tasks' | 'reminders';
  setSessionTab: (tab: 'teams' | 'notes' | 'tasks' | 'reminders') => void;
  showTasksTooltip?: boolean;
  setShowTasksTooltip?: (show: boolean) => void;
  showEndWarningMessage?: boolean;
  beepSoundEnabled?: boolean;
  setBeepSoundEnabled?: (enabled: boolean) => void;
  beepWarningTime?: number;
  breakRemindersEnabled?: boolean;
  setBreakRemindersEnabled?: (enabled: boolean) => void;
  notes: Array<{
    id: string;
    text: string;
    createdAt: Date;
  }>;
  newNote: string;
  setNewNote: (note: string) => void;
  tasks: Array<{
    id: string;
    text?: string;
    title?: string;
    description?: string;
    status?: 'todo' | 'inprogress' | 'done';
    completed?: boolean;
    createdAt: Date | string;
    created_by?: { id: string; name: string } | string;
    assigned_to?: { id: string; name: string } | string | null;
    assignedTo?: { id: string; name: string; avatar: string } | null;
    is_public?: boolean;
  }>;
  newTask: string;
  setNewTask: (task: string) => void;
  reminders: Array<{
    id: string;
    text: string;
    time: string;
    completed: boolean;
  }>;
  setReminders: React.Dispatch<React.SetStateAction<Array<{
    id: string;
    text: string;
    time: string;
    completed: boolean;
  }>>>;
  teamMembers: Array<{
    id: string;
    name: string;
    avatar: string;
    isOnline: boolean;
    isMuted: boolean;
  }>;
  sharedTasks: Array<{
    id: string;
    text?: string;
    title?: string;
    description?: string;
    status?: 'todo' | 'inprogress' | 'done';
    completed?: boolean;
    createdBy: string;
    createdAt: Date;
    assigned_to?: string;
    assignedTo?: { id: string; name: string; avatar: string } | null;
    is_public?: boolean;
  }>;
  sharedNotes: Array<{
    id: string;
    text: string;
    createdBy: string;
    createdAt: Date;
  }>;
  isMuted: boolean;
  isScreenSharing: boolean;
  screenShareStreamRef?: React.RefObject<MediaStream | null>;
  musicVolume: number;
  isMusicPlaying: boolean;
  isMusicMuted: boolean;
  formatTime: (seconds: number) => string;
  onClose: () => void;
  onConfirmPreSession: () => void;
  onPauseSession: () => void;
  onResumeSession: () => void;
  onEndSession: () => void;
  onConfirmEndSession: () => void;
  onExtendSession: (minutes: number) => void;
  onAddNote: () => void;
  onEditNote: (noteId: string) => void;
  onDeleteNote: (noteId: string) => void;
  onOpenNoteModal: (note: { id: string; text: string; createdAt: Date }) => void;
  onAddTask: (assignedTo?: { id: string; name: string; avatar: string } | null) => void;
  onToggleTask: (taskId: string) => void;
  onEditTask: (taskId: string) => void;
  onDeleteTask: (taskId: string) => void;
  onToggleMute: () => void;
  onToggleScreenShare: () => void;
  onToggleSharedTask: (taskId: string) => void;
  onDeleteSharedTask: (taskId: string) => void;
  onSetSelectedNote: (note: { id: string; text: string; createdAt: Date }) => void;
  onSetShowNoteModal: (show: boolean) => void;
  onSetShowMembersPopup: (show: boolean) => void;
  onSetMusicVolume: (volume: number) => void;
  onSetIsMusicPlaying: (playing: boolean) => void;
  onSetIsMusicMuted: (muted: boolean) => void;
  onChangeMusic?: (music: string) => void;
  noiseAudioRef: React.RefObject<HTMLAudioElement | null>;
  natureAudioRef: React.RefObject<HTMLAudioElement | null>;
  binauralAudioRef: React.RefObject<HTMLAudioElement | null>;
  youtubePlayerRef: React.RefObject<any>;
  missedBreakCount?: number;
  pipWindow: Window | null;
  requestPiP: (width: number, height: number) => Promise<void>;
  closePiP: () => void;
  isPiPSupported: boolean;
  checkAutoPiPPermission: () => Promise<string>;
}

const FocusSessionModal: React.FC<FocusSessionModalProps> = ({
  open,
  activeMode,
  sessionData,
  currentSession,
  sessionTimeLeft,
  sessionStarted,
  sessionPaused,
  sessionCompleted,
  sessionTab,
  setSessionTab,
  showTasksTooltip = false,
  setShowTasksTooltip,
  showEndWarningMessage = false,
  beepSoundEnabled = true,
  setBeepSoundEnabled,
  beepWarningTime = 59,
  breakRemindersEnabled = true,
  setBreakRemindersEnabled,
  notes,
  newNote,
  setNewNote,
  tasks,
  newTask,
  setNewTask,
  reminders,
  setReminders,
  teamMembers,
  sharedTasks,
  sharedNotes,
  isMuted,
  isScreenSharing,
  screenShareStreamRef,
  musicVolume,
  isMusicPlaying,
  isMusicMuted,
  formatTime,
  onClose,
  onConfirmPreSession,
  onPauseSession,
  onResumeSession,
  onEndSession,
  onConfirmEndSession,
  onExtendSession,
  onAddNote,
  onEditNote,
  onDeleteNote,
  onOpenNoteModal,
  onAddTask,
  onToggleTask,
  onEditTask,
  onDeleteTask,
  onToggleMute,
  onToggleScreenShare,
  onToggleSharedTask,
  onDeleteSharedTask,
  onSetSelectedNote,
  onSetShowNoteModal,
  onSetShowMembersPopup,
  onSetMusicVolume,
  onSetIsMusicPlaying,
  onSetIsMusicMuted,
  onChangeMusic,
  noiseAudioRef,
  natureAudioRef,
  binauralAudioRef,
  youtubePlayerRef,
  missedBreakCount = 0,
  pipWindow,
  requestPiP,
  closePiP,
  isPiPSupported,
  checkAutoPiPPermission,
}) => {
  const [showReactivatePiPTooltip, setShowReactivatePiPTooltip] = useState(false);
  const prevPipWindow = useRef<Window | null>(null);

  // Lock scroll during focus session (prevents background scroll even if fixed)
  useScrollLock(open);

  // Handle tooltip when PiP closes externally
  useEffect(() => {
    if (prevPipWindow.current && !pipWindow && sessionStarted && !sessionCompleted) {
      setShowReactivatePiPTooltip(true);
      setTimeout(() => setShowReactivatePiPTooltip(false), 5000);
    }
    prevPipWindow.current = pipWindow;
  }, [pipWindow, sessionStarted, sessionCompleted]);

  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showPiPConfirmEnd, setShowPiPConfirmEnd] = useState(false);
  const [showPiPBlockedModal, setShowPiPBlockedModal] = useState(false);
  const [extendMinutes, setExtendMinutes] = useState(15);
  const [taskAssignedTo, setTaskAssignedTo] = useState<{ id: string; name: string; avatar: string } | null>(null);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const taskInputRef = useRef<HTMLInputElement>(null);
  const taskInputContainerRef = useRef<HTMLDivElement>(null);
  const [showMusicSelector, setShowMusicSelector] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const musicSelectorRef = useRef<HTMLDivElement>(null);
  const timePickerRef = useRef<HTMLDivElement>(null);

  // Attempt Auto-PiP on Tab Switch
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden' && !pipWindow && isPiPSupported && sessionStarted && !sessionCompleted) {
        try {
          await requestPiP(320, 130);
        } catch (error) {
          console.warn('Auto-PiP on switch failed:', error);
          setShowPiPBlockedModal(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pipWindow, isPiPSupported, sessionStarted, sessionCompleted, requestPiP]);

  // Attempt Auto-PiP on Session Start (Mount) - Tries to use the "Start" click gesture
  useEffect(() => {
    if (open && sessionStarted && !pipWindow && isPiPSupported) {
      // Small timeout to ensure we don't block the render, but close enough to the click??
      // Actually, immediate is better for gesture token retention.
      // We'll try only if the user hasn't explicitly disabled "Always PiP" (future feature).
      // For now, we only do it if the user wants "Auto PiP".

      // NOTE: Uncomment the line below if you want the window to pop OPEN immediately on start.
      // requestPiP(360, 200).catch(e => console.warn("Auto-start PiP failed", e));
    }
  }, [open, sessionStarted]);

  // Register Media Session Handler for PiP
  useEffect(() => {
    if ('mediaSession' in navigator) {
      if (sessionStarted && !sessionCompleted && !pipWindow) {
        try {
          navigator.mediaSession.setActionHandler('enterpictureinpicture' as any, async () => {
            console.log('Media Session Open PiP Triggered');
            await requestPiP(320, 130);
          });
        } catch (error) {
          console.warn('Media Session API error:', error);
        }
      } else {
        // Cleanup or unregister handler if needed
        try {
          navigator.mediaSession.setActionHandler('enterpictureinpicture' as any, null);
        } catch (e) { }
      }
    }
  }, [sessionStarted, sessionCompleted, pipWindow, requestPiP]);

  useEffect(() => {
    if (showExtendModal && setShowTasksTooltip) {
      setShowTasksTooltip(false);
    }
  }, [showExtendModal, setShowTasksTooltip]);

  // Close music selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (musicSelectorRef.current && !musicSelectorRef.current.contains(event.target as Node)) {
        setShowMusicSelector(false);
      }
      if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
        setShowTimePicker(false);
      }
      if (taskInputContainerRef.current && showMentionDropdown && !taskInputContainerRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false);
      }
    };

    if (showMusicSelector || showTimePicker || showMentionDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMusicSelector, showTimePicker, showMentionDropdown]);

  const handleTasksTabClick = () => {
    setSessionTab('tasks');
  };

  const handleCloseTooltip = () => {
    if (setShowTasksTooltip) {
      setShowTasksTooltip(false);
    }
  };

  const handleExtendSession = (minutes: number) => {
    onExtendSession(minutes);
    setShowExtendModal(false);
  };
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-[#FFF8F0] z-50 overflow-y-auto">
      <div className="min-h-full flex flex-col">
        {/* Session Header */}
        <div className="bg-white border-b border-gray-200/60 p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-center">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-medium text-gray-900 mb-1 sm:mb-2 truncate">
                Focus Session
              </h2>
              <p className="text-sm text-gray-600 truncate font-light">
                Goal: {sessionData.goal || (currentSession?.goal || 'Focus Session')}
              </p>
            </div>
          </div>
        </div>

        {/* Timer and Controls */}
        <div className="bg-white border border-gray-200/60 rounded-2xl p-6 sm:p-8 mx-4 sm:mx-6 mb-6 mt-6 sm:mt-8 flex-shrink-0 shadow-sm">
          {/* PiP Portal */}
          {pipWindow && createPortal(
            <div className="flex flex-col min-h-screen bg-[#FFF8F0] overflow-hidden p-3 relative">
              {/* Top Row: Goal (Left) and Notification + Controls (Right Group) */}
              <div className="flex items-center justify-between w-full mb-1">
                {/* Left Side: Goal */}
                <h2 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate max-w-[160px]">
                  {sessionData.goal || (currentSession?.goal || 'Focus Session')}
                </h2>

                {/* Right Side Group: Notification + Restore */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {missedBreakCount > 0 && (
                    <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-50 border border-red-100 animate-pulse">
                      <FaBell className="text-[10px] text-red-500" />
                      <span className="text-[10px] font-bold text-red-500 leading-none">{missedBreakCount} Missed</span>
                    </div>
                  )}

                  <button
                    onClick={closePiP}
                    className="p-1 -mr-1 hover:bg-orange-50 text-gray-400 hover:text-orange-600 transition-colors rounded-md"
                    title="Back to Dashboard"
                  >
                    <MdLaunch className="text-sm" />
                  </button>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 flex items-center justify-center relative">
                {sessionCompleted ? (
                  <div className="flex items-center gap-3 animate-in fade-in zoom-in duration-500 bg-orange-50/50 px-4 py-2 rounded-xl border border-orange-100">
                    <FaTrophy className="text-xl text-orange-500" />
                    <span className="text-lg font-semibold text-gray-900 whitespace-nowrap">Session Completed!</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full max-w-xs gap-4">
                    {/* Timer */}
                    <div className="text-[44px] font-bold text-gray-900 font-mono tracking-tighter leading-none">
                      {formatTime(sessionTimeLeft)}
                    </div>

                    {/* Quick Controls */}
                    <div className="flex items-center gap-2">
                      {sessionPaused ? (
                        <button
                          onClick={onResumeSession}
                          className="w-11 h-11 flex items-center justify-center bg-orange-600 hover:bg-orange-700 text-white rounded-full transition-all shadow-sm active:scale-95"
                          title="Resume"
                        >
                          <BsFillPlayBtnFill className="text-xl" />
                        </button>
                      ) : (
                        <button
                          onClick={onPauseSession}
                          className="w-11 h-11 flex items-center justify-center bg-white border border-orange-200 text-orange-600 hover:bg-orange-50 rounded-full transition-all active:scale-95"
                          title="Pause"
                        >
                          <BsPauseBtnFill className="text-xl" />
                        </button>
                      )}

                      <button
                        onClick={() => setShowPiPConfirmEnd(true)}
                        className="w-11 h-11 flex items-center justify-center bg-white border border-red-200 text-red-500 hover:bg-red-50 rounded-full transition-all active:scale-95"
                        title="End Session"
                      >
                        <FaCircleStop className="text-2xl text-gray-600" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Enhanced Full-Window Confirmation Overlay - Equal Margins on all sides */}
              {showPiPConfirmEnd && !sessionCompleted && (
                <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-50 animate-in fade-in duration-200">
                  <div className="absolute inset-4 bg-white p-4 rounded-2xl shadow-xl border border-gray-100/60 text-center flex flex-col items-center justify-center transform animate-in zoom-in duration-300">
                    <p className="text-[10px] font-bold text-gray-900 mb-2 uppercase tracking-tighter">End session early?</p>
                    <div className="flex flex-col gap-1.5 w-full">
                      <button
                        onClick={() => {
                          onConfirmEndSession();
                          setShowPiPConfirmEnd(false);
                          closePiP();
                        }}
                        className="w-full py-1.5 text-[10px] font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all shadow-sm active:scale-95"
                      >
                        Yes, end session
                      </button>
                      <button
                        onClick={() => setShowPiPConfirmEnd(false)}
                        className="w-full py-1.5 text-[10px] font-medium bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 hover:text-gray-700 transition-colors"
                      >
                        Keep going
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>,
            pipWindow.document.body
          )}

          <div className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-6 sm:gap-0 ${pipWindow ? 'opacity-50 pointer-events-none filter blur-sm transition-all' : ''}`}>
            <div className="flex-1 text-center sm:text-left">
              <div className="text-4xl sm:text-5xl md:text-6xl font-medium text-gray-900 mb-2">
                {formatTime(sessionTimeLeft)}
              </div>
              <div className="flex items-center justify-center sm:justify-start gap-3 flex-wrap">
                {sessionStarted && ((currentSession?.duration || sessionData.duration) > 25) && (sessionTimeLeft > 1500) && (
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-orange-50 border border-orange-200/50 rounded-full">
                    <svg className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className={`text-xs font-medium text-orange-600 ${(currentSession?.duration || sessionData.duration) <= 25 ? 'opacity-50' : ''}`}>
                      Gentle check-ins
                    </span>
                    <button
                      onClick={() => setBreakRemindersEnabled && setBreakRemindersEnabled(!breakRemindersEnabled)}
                      disabled={(currentSession?.duration || sessionData.duration) <= 25}
                      className={`ml-1 relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${breakRemindersEnabled ? 'bg-orange-600' : 'bg-gray-300'} ${(currentSession?.duration || sessionData.duration) <= 25 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        }`}
                      aria-label="Toggle gentle check-ins"
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${breakRemindersEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                          }`}
                      />
                    </button>
                  </div>
                )}
                {sessionStarted && (
                  <div className="flex items-center gap-2 px-2.5 py-1 bg-orange-50 border border-orange-200/50 rounded-full">
                    <svg className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs font-medium text-orange-600">
                      End-of-session alert {beepSoundEnabled ? `at ${beepWarningTime}s` : 'disabled'}
                    </span>
                    <button
                      onClick={() => setBeepSoundEnabled && setBeepSoundEnabled(!beepSoundEnabled)}
                      className={`ml-1 relative inline-flex h-4 w-7 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${beepSoundEnabled ? 'bg-orange-600' : 'bg-gray-300'
                        }`}
                      aria-label="Toggle beep sound"
                    >
                      <span
                        className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${beepSoundEnabled ? 'translate-x-3.5' : 'translate-x-0.5'
                          }`}
                      />
                    </button>
                  </div>
                )}
                {sessionStarted && !sessionCompleted && (
                  <button
                    onClick={() => setShowExtendModal(true)}
                    className="flex items-center gap-2 px-2.5 py-1 bg-orange-50 border border-orange-200/50 hover:bg-orange-100/50 rounded-full transition-all duration-200"
                  >
                    <svg className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs font-medium text-orange-600">Extend time</span>
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap justify-center sm:justify-end gap-3">
              {!sessionStarted ? (
                <button
                  onClick={onConfirmPreSession}
                  className={`px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 text-sm sm:text-base ${sessionCompleted
                    ? 'bg-white border border-orange-600 hover:bg-orange-50 text-orange-600'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                    }`}
                >
                  <BsFillPlayBtnFill className="text-xl" />
                  <span>{sessionCompleted ? 'Repeat Session' : 'Start Session'}</span>
                </button>
              ) : sessionPaused ? (
                <button
                  onClick={onResumeSession}
                  className="p-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-all duration-300 flex items-center justify-center"
                  title="Resume"
                >
                  <BsFillPlayBtnFill className="text-xl" />
                </button>
              ) : (
                <button
                  onClick={onPauseSession}
                  className="p-3 bg-white border border-orange-200/60 hover:border-orange-300/80 text-orange-600 hover:bg-orange-50/30 rounded-lg transition-all duration-300 flex items-center justify-center"
                  title="Pause"
                >
                  <BsPauseBtnFill className="text-xl" />
                </button>
              )}

              {sessionStarted && !sessionCompleted && (
                <>
                  {isPiPSupported && !pipWindow && (
                    <TooltipProvider>
                      <Tooltip open={showReactivatePiPTooltip} onOpenChange={setShowReactivatePiPTooltip}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              requestPiP(320, 130);
                              setShowReactivatePiPTooltip(false);
                            }}
                            className="p-3 bg-white border border-gray-200/60 hover:border-orange-300/80 text-gray-600 hover:text-orange-600 hover:bg-orange-50/30 rounded-lg transition-all duration-300 flex items-center justify-center"
                            title="Open Mini Player"
                          >
                            <BsPip className="text-xl" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-orange-600 text-white border-0">
                          <p>Reactivate Mini Mode</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </>
              )}

              {!sessionCompleted && (
                <button
                  onClick={onEndSession}
                  className="p-3 bg-white border border-red-600 hover:border-red-700 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 flex items-center justify-center"
                  title="End Session"
                >
                  <FaCircleStop className="text-xl text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Placeholder message when PiP is active */}
          {pipWindow && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-[110] backdrop-blur-[2px] rounded-2xl">
              <div className="text-orange-600 mb-2">
                <BsPip className="text-4xl" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Playing in Mini Mode</h3>
              <button
                onClick={closePiP}
                className="mt-2 text-sm text-orange-600 hover:text-orange-700 font-medium hover:underline"
              >
                Restore to Dashboard
              </button>
            </div>
          )}
        </div>

        {/* Music Player - Only for Solo Mode */}
        {activeMode === 'solo' && (
          <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-5 mx-4 sm:mx-6 mb-6 flex-shrink-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
              <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0 relative" ref={musicSelectorRef}>
                <button
                  onClick={() => sessionStarted && onChangeMusic && setShowMusicSelector(!showMusicSelector)}
                  className={`flex items-center space-x-2 transition-colors group ${sessionStarted && onChangeMusic
                    ? 'text-gray-700 hover:text-orange-600 cursor-pointer'
                    : 'text-gray-700 cursor-default'
                    }`}
                  disabled={!sessionStarted || !onChangeMusic}
                >
                  <span className="text-sm font-medium truncate">
                    {(() => {
                      const music = currentSession?.music || sessionData.music;
                      if (music === 'silence') return 'Silence Mode';
                      if (music === 'nature') return 'Nature Sounds';
                      if (music === 'binaural') return 'Binaural Beats';
                      if (music === 'noise') return 'White Noise';
                      if (music === 'custom') return 'Custom YouTube Music';
                      return 'Silence Mode';
                    })()}
                  </span>
                  {sessionStarted && onChangeMusic && (
                    <svg className={`w-4 h-4 text-gray-400 group-hover:text-orange-600 transition-colors ${showMusicSelector ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {/* Music Selector Dropdown */}
                {showMusicSelector && sessionStarted && onChangeMusic && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200/60 rounded-lg shadow-lg z-50 min-w-[180px]">
                    <div className="py-1">
                      {[
                        { value: 'silence', label: 'Silence Mode' },
                        { value: 'nature', label: 'Nature Sounds' },
                        { value: 'binaural', label: 'Binaural Beats' },
                        { value: 'noise', label: 'White Noise' },
                        { value: 'custom', label: 'Custom YouTube Music' }
                      ].map((option) => {
                        const currentMusic = currentSession?.music || sessionData.music;
                        const isSelected = option.value === currentMusic;
                        return (
                          <button
                            key={option.value}
                            onClick={() => {
                              onChangeMusic(option.value);
                              setShowMusicSelector(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${isSelected
                              ? 'bg-orange-50 text-orange-600 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Music Rhythm Animation */}
              {!sessionCompleted && (() => {
                const music = currentSession?.music || sessionData.music;
                return music !== 'silence';
              })() && (
                  <div className="flex items-center justify-center flex-1 space-x-0.5 sm:space-x-1">
                    <div className="flex space-x-0.5 sm:space-x-1 items-end h-12">
                      {(() => {
                        const music = currentSession?.music || sessionData.music;
                        const shouldAnimate = isMusicPlaying;
                        const barHeights = ['16px', '20px', '12px', '24px', '18px', '22px', '14px', '19px', '16px', '21px', '10px', '26px'];
                        const delays = ['0s', '0.1s', '0.2s', '0.3s', '0.4s', '0.5s', '0.6s', '0.7s', '0.8s', '0.9s', '1.0s', '1.1s'];
                        const durations = ['0.6s', '0.7s', '0.5s', '0.8s', '0.65s', '0.75s', '0.55s', '0.7s', '0.6s', '0.72s', '0.5s', '0.85s'];

                        return barHeights.map((height, index) => (
                          <div
                            key={index}
                            className="w-0.5 sm:w-1 bg-orange-500/40 rounded-full"
                            style={{
                              height: height,
                              animation: shouldAnimate ? `rhythm-bar ${durations[index]} ease-in-out infinite` : 'none',
                              animationDelay: shouldAnimate ? delays[index] : '0s',
                              transformOrigin: 'bottom',
                              opacity: shouldAnimate ? 1 : 0.4
                            }}
                          />
                        ));
                      })()}
                    </div>
                  </div>
                )}

              <div className="flex items-center space-x-2 sm:space-x-3 flex-1 justify-end">
                {(() => {
                  const music = currentSession?.music || sessionData.music;
                  const isDisabled = music === 'silence' || sessionPaused || sessionCompleted;

                  // Play/Pause handler
                  const handlePlayPause = () => {
                    if (isDisabled) return;

                    if (music === 'noise' && noiseAudioRef.current) {
                      // Stop other tracks before playing
                      if (!isMusicPlaying) {
                        if (natureAudioRef.current) {
                          natureAudioRef.current.pause();
                          natureAudioRef.current.currentTime = 0;
                        }
                        if (binauralAudioRef.current) {
                          binauralAudioRef.current.pause();
                          binauralAudioRef.current.currentTime = 0;
                        }
                        if (youtubePlayerRef.current) {
                          try {
                            youtubePlayerRef.current.pauseVideo();
                          } catch (error) {
                            console.error('Error pausing YouTube player:', error);
                          }
                        }
                      }
                      if (isMusicPlaying) {
                        noiseAudioRef.current.pause();
                        onSetIsMusicPlaying(false);
                      } else {
                        noiseAudioRef.current.play().catch(error => {
                          console.error('Error playing noise audio:', error);
                        });
                        onSetIsMusicPlaying(true);
                      }
                    } else if (music === 'nature' && natureAudioRef.current) {
                      // Stop other tracks before playing
                      if (!isMusicPlaying) {
                        if (noiseAudioRef.current) {
                          noiseAudioRef.current.pause();
                          noiseAudioRef.current.currentTime = 0;
                        }
                        if (binauralAudioRef.current) {
                          binauralAudioRef.current.pause();
                          binauralAudioRef.current.currentTime = 0;
                        }
                        if (youtubePlayerRef.current) {
                          try {
                            youtubePlayerRef.current.pauseVideo();
                          } catch (error) {
                            console.error('Error pausing YouTube player:', error);
                          }
                        }
                      }
                      if (isMusicPlaying) {
                        natureAudioRef.current.pause();
                        onSetIsMusicPlaying(false);
                      } else {
                        natureAudioRef.current.play().catch(error => {
                          console.error('Error playing nature audio:', error);
                        });
                        onSetIsMusicPlaying(true);
                      }
                    } else if (music === 'binaural' && binauralAudioRef.current) {
                      // Stop other tracks before playing
                      if (!isMusicPlaying) {
                        if (noiseAudioRef.current) {
                          noiseAudioRef.current.pause();
                          noiseAudioRef.current.currentTime = 0;
                        }
                        if (natureAudioRef.current) {
                          natureAudioRef.current.pause();
                          natureAudioRef.current.currentTime = 0;
                        }
                        if (youtubePlayerRef.current) {
                          try {
                            youtubePlayerRef.current.pauseVideo();
                          } catch (error) {
                            console.error('Error pausing YouTube player:', error);
                          }
                        }
                      }
                      if (isMusicPlaying) {
                        binauralAudioRef.current.pause();
                        onSetIsMusicPlaying(false);
                      } else {
                        binauralAudioRef.current.play().catch(error => {
                          console.error('Error playing binaural audio:', error);
                        });
                        onSetIsMusicPlaying(true);
                      }
                    } else if (music === 'custom' && youtubePlayerRef.current) {
                      // Stop all default audio tracks before playing YouTube
                      if (!isMusicPlaying) {
                        if (noiseAudioRef.current) {
                          noiseAudioRef.current.pause();
                          noiseAudioRef.current.currentTime = 0;
                        }
                        if (natureAudioRef.current) {
                          natureAudioRef.current.pause();
                          natureAudioRef.current.currentTime = 0;
                        }
                        if (binauralAudioRef.current) {
                          binauralAudioRef.current.pause();
                          binauralAudioRef.current.currentTime = 0;
                        }
                      }
                      try {
                        if (isMusicPlaying) {
                          youtubePlayerRef.current.pauseVideo();
                          onSetIsMusicPlaying(false);
                        } else {
                          youtubePlayerRef.current.playVideo();
                          onSetIsMusicPlaying(true);
                        }
                      } catch (error) {
                        console.error('Error toggling YouTube video:', error);
                      }
                    }
                  };

                  // Mute/Unmute handler
                  const handleMuteUnmute = () => {
                    if (isDisabled) return;

                    if (music === 'noise' && noiseAudioRef.current) {
                      noiseAudioRef.current.muted = !isMusicMuted;
                      onSetIsMusicMuted(!isMusicMuted);
                    } else if (music === 'nature' && natureAudioRef.current) {
                      natureAudioRef.current.muted = !isMusicMuted;
                      onSetIsMusicMuted(!isMusicMuted);
                    } else if (music === 'binaural' && binauralAudioRef.current) {
                      binauralAudioRef.current.muted = !isMusicMuted;
                      onSetIsMusicMuted(!isMusicMuted);
                    } else if (music === 'custom' && youtubePlayerRef.current) {
                      try {
                        if (isMusicMuted) {
                          youtubePlayerRef.current.unMute();
                          onSetIsMusicMuted(false);
                        } else {
                          youtubePlayerRef.current.mute();
                          onSetIsMusicMuted(true);
                        }
                      } catch (error) {
                        console.error('Error toggling YouTube mute:', error);
                      }
                    }
                  };

                  return (
                    <>
                      {/* Play/Pause Button */}
                      <button
                        onClick={handlePlayPause}
                        disabled={isDisabled}
                        className={`p-2 rounded-lg transition-all duration-300 ${isDisabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50/50'
                          }`}
                        title={isMusicPlaying ? 'Pause' : 'Play'}
                      >
                        {isMusicPlaying ? (
                          <BsPauseBtnFill className="text-lg" />
                        ) : (
                          <BsFillPlayBtnFill className="text-lg" />
                        )}
                      </button>

                      {/* Mute/Unmute Button */}
                      <button
                        onClick={handleMuteUnmute}
                        disabled={isDisabled}
                        className={`p-2 rounded-lg transition-all duration-300 ${isDisabled
                          ? 'text-gray-400 cursor-not-allowed opacity-50'
                          : 'text-orange-600 hover:text-orange-700 hover:bg-orange-50/50'
                          }`}
                        title={isMusicMuted ? 'Unmute' : 'Mute'}
                      >
                        {isMusicMuted ? (
                          <FaVolumeMute className="text-lg" />
                        ) : (
                          <FaVolumeUp className="text-lg" />
                        )}
                      </button>

                      <span className={`text-xs ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>Volume:</span>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={musicVolume}
                        onChange={(e) => onSetMusicVolume(parseInt(e.target.value))}
                        disabled={isDisabled}
                        className={`w-16 sm:w-20 h-1.5 rounded-lg appearance-none ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{
                          background: `linear-gradient(to right, #ea580c 0%, #ea580c ${musicVolume}%, #e5e7eb ${musicVolume}%, #e5e7eb 100%)`,
                          opacity: isDisabled ? 0.5 : 1
                        }}
                      />
                      <span className={`text-xs min-w-[2.5rem] text-right ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>{musicVolume}%</span>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Team Mode: Google Meet-style Meeting Controls */}
        {activeMode === 'team' && sessionStarted && !sessionCompleted && (
          <div className="mx-4 sm:mx-6 mb-6">
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Meeting controls</p>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
                <button
                  onClick={onToggleMute}
                  className={`flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all text-sm ${isMuted
                    ? 'bg-red-50 text-red-600 border border-red-200/60 hover:bg-red-100/50'
                    : 'bg-orange-50 text-orange-600 border border-orange-200/60 hover:bg-orange-100/50'
                    }`}
                  title={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                >
                  {isMuted ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" /></svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                  )}
                  <span>{isMuted ? 'Unmute' : 'Mic'}</span>
                </button>
                <button
                  onClick={onToggleScreenShare}
                  className={`flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all text-sm ${isScreenSharing
                    ? 'bg-red-50 text-red-600 border border-red-200/60 hover:bg-red-100/50'
                    : 'bg-orange-50 text-orange-600 border border-orange-200/60 hover:bg-orange-100/50'
                    }`}
                  title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  <span>{isScreenSharing ? 'Stop share' : 'Share screen'}</span>
                </button>
                <button
                  onClick={() => onSetShowMembersPopup(true)}
                  className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 rounded-xl font-medium transition-all text-sm bg-orange-50 text-orange-600 border border-orange-200/60 hover:bg-orange-100/50"
                  title="Participants"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
                  <span>Participants ({teamMembers.length})</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Screen Share Preview - when sharing in team mode */}
        {activeMode === 'team' && isScreenSharing && screenShareStreamRef?.current && (
          <div className="mx-4 sm:mx-6 mb-6">
            <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-700 shadow-lg">
              <p className="text-xs font-medium text-gray-400 px-4 py-2 border-b border-gray-700">You're sharing your screen</p>
              <ScreenSharePreview streamRef={screenShareStreamRef} />
            </div>
          </div>
        )}

        {/* Tabbed Interface */}
        <div className="mx-4 sm:mx-6 mb-6">
          {/* Tab Navigation */}
          <div className="mb-6 flex-shrink-0 w-full">
            <div className="flex w-full border-b border-gray-200/60">
              {activeMode === 'team' && (
                <button
                  onClick={() => setSessionTab('teams')}
                  className={`flex-1 py-3 font-medium transition-all duration-200 text-sm text-center relative focus:outline-none focus:ring-0 ${sessionTab === 'teams'
                    ? 'text-orange-600'
                    : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Team
                  {sessionTab === 'teams' && (
                    <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-orange-600"></div>
                  )}
                </button>
              )}
              <TooltipProvider>
                <Tooltip open={showTasksTooltip && sessionTab === 'tasks'}>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleTasksTabClick}
                      className={`flex-1 py-3 font-medium transition-all duration-200 text-sm text-center relative focus:outline-none focus:ring-0 ${sessionTab === 'tasks'
                        ? 'text-orange-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Focus Tasks
                      {sessionTab === 'tasks' && (
                        <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-orange-600"></div>
                      )}
                    </button>
                  </TooltipTrigger>
                  {/* @ts-ignore - TooltipContent accepts children but TypeScript types are incomplete */}
                  <TooltipContent
                    side="bottom"
                    usePortal={false}
                    className="bg-orange-600 text-white px-4 py-3 text-sm max-w-xs z-[100] shadow-lg relative"
                  >
                    <button
                      onClick={handleCloseTooltip}
                      className="absolute top-2 right-2 text-white hover:text-orange-200 transition-colors focus:outline-none"
                      aria-label="Close tooltip"
                    >
                      <IoClose className="w-4 h-4" />
                    </button>
                    <p className="font-medium leading-relaxed pr-6">
                      Add your tasks first! Organizing them now makes your work flow smoother and keeps you laser-focused throughout the session.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                onClick={() => setSessionTab('notes')}
                className={`flex-1 py-3 font-medium transition-all duration-200 text-sm text-center relative focus:outline-none focus:ring-0 ${sessionTab === 'notes'
                  ? 'text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Quick Notes
                {sessionTab === 'notes' && (
                  <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-orange-600"></div>
                )}
              </button>
              <button
                onClick={() => setSessionTab('reminders')}
                className={`flex-1 py-3 font-medium transition-all duration-200 text-sm text-center relative focus:outline-none focus:ring-0 ${sessionTab === 'reminders'
                  ? 'text-orange-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Check-ins
                {sessionTab === 'reminders' && (
                  <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-orange-600"></div>
                )}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="bg-white border border-gray-200/60 rounded-xl p-4 sm:p-6 w-full">
            {/* Notes Tab */}
            {sessionTab === 'notes' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                    Quick Notes
                  </h3>
                </div>

                {/* Add Note */}
                <div className="space-y-3 mb-6">
                  <textarea
                    value={newNote}
                    onChange={(e) => {
                      setNewNote(e.target.value);
                      // Auto-resize textarea
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                    }}
                    placeholder="Add a new note... (Press Ctrl+Enter to save)"
                    className="w-full bg-white text-gray-900 rounded-lg p-3 border border-gray-200/60 focus:border-orange-300 focus:ring-orange-200 focus:ring-opacity-50 focus:outline-none resize-none min-h-[80px] max-h-[200px] overflow-y-auto text-sm placeholder:text-gray-400"
                    onKeyDown={(e) => {
                      if (e.ctrlKey && e.key === 'Enter') {
                        onAddNote();
                      }
                    }}
                  />
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    {activeMode === 'team' && (
                      <label className="flex items-center space-x-2 text-gray-600">
                        <input
                          type="checkbox"
                          id="shareNote"
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                        />
                        <span className="text-sm">Share with team</span>
                      </label>
                    )}
                    <button
                      onClick={onAddNote}
                      className="px-6 py-2.5 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors text-sm"
                    >
                      Add Note
                    </button>
                  </div>
                </div>

                {/* Notes List */}
                <div className="space-y-3">
                  {notes.map((note) => (
                    <div key={note.id} className="flex items-start space-x-3 p-4 bg-white border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm transition-all cursor-pointer" onClick={() => onOpenNoteModal(note)}>
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-900 text-sm line-clamp-2">{note.text}</div>

                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditNote(note.id);
                        }}
                        className="text-gray-400 hover:text-orange-600 text-sm p-1 flex-shrink-0 transition-colors"
                        title="Edit note"
                      >
                        <MdEdit className="text-base" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteNote(note.id);
                        }}
                        className="text-gray-400 hover:text-orange-600 text-sm p-1 flex-shrink-0 transition-colors"
                        title="Delete note"
                      >
                        <RiDeleteBin4Fill className="text-base" />
                      </button>
                    </div>
                  ))}
                  {notes.length === 0 && (
                    <div className="text-gray-500 text-center py-12 text-sm">No notes yet.</div>
                  )}
                </div>
              </div>
            )}

            {/* Tasks Tab */}
            {sessionTab === 'tasks' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                    Focus Tasks
                  </h3>
                </div>

                {/* Add Task */}
                <div className="space-y-3 mb-6">
                  <div className="relative" ref={taskInputContainerRef}>
                    <input
                      ref={taskInputRef}
                      type="text"
                      value={newTask}
                      onChange={(e) => {
                        const val = e.target.value;
                        setNewTask(val);
                        // Detect @mention: show dropdown when @ is typed
                        const atIdx = val.lastIndexOf('@');
                        if (atIdx !== -1) {
                          const afterAt = val.slice(atIdx + 1);
                          if (!afterAt.includes(' ')) {
                            setShowMentionDropdown(true);
                            setMentionFilter(afterAt.toLowerCase());
                          } else {
                            setShowMentionDropdown(false);
                          }
                        } else {
                          setShowMentionDropdown(false);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (showMentionDropdown && (e.key === 'Escape' || e.key === 'Tab')) {
                          setShowMentionDropdown(false);
                        }
                        if (e.key === 'Enter' && !showMentionDropdown) {
                          const assignee = taskAssignedTo ?? undefined;
                          onAddTask(assignee);
                          setTaskAssignedTo(null);
                        }
                      }}
                      placeholder={activeMode === 'team' ? "Add a new task... Type @ to assign to someone" : "Add a new task..."}
                      className="w-full bg-white text-gray-900 rounded-lg p-3 border border-gray-200/60 focus:border-orange-300 focus:ring-orange-200 focus:ring-opacity-50 focus:outline-none text-sm placeholder:text-gray-400"
                    />
                    {activeMode === 'team' && showMentionDropdown && (
                      <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200/60 rounded-xl shadow-lg py-2 max-h-48 overflow-y-auto">
                        <div className="px-3 py-1.5 text-xs text-gray-500 font-medium border-b border-gray-100">Assign to a team member</div>
                        {teamMembers
                          .filter((m) => m.isOnline && (!mentionFilter || m.name.toLowerCase().includes(mentionFilter)))
                          .map((member) => (
                            <button
                              key={member.id}
                              type="button"
                              onClick={() => {
                                setTaskAssignedTo(member);
                                setShowMentionDropdown(false);
                                const atIdx = newTask.lastIndexOf('@');
                                if (atIdx !== -1) {
                                  setNewTask(newTask.slice(0, atIdx).trim());
                                }
                              }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50 transition-colors text-left"
                            >
                              <div className="w-8 h-8 bg-orange-100 border border-orange-200/60 rounded-full flex items-center justify-center text-orange-600 font-medium text-xs flex-shrink-0">
                                {member.avatar}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-900">{member.name}</span>
                                <span className="text-xs text-orange-500 ml-1">● Online</span>
                              </div>
                            </button>
                          ))}
                        {teamMembers.filter((m) => m.isOnline && (!mentionFilter || m.name.toLowerCase().includes(mentionFilter))).length === 0 && (
                          <div className="px-3 py-4 text-sm text-gray-500 text-center">No matching team members</div>
                        )}
                      </div>
                    )}
                  </div>
                  {activeMode === 'team' && taskAssignedTo && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 border border-orange-200/60 rounded-lg text-sm text-orange-700">
                        <span className="w-5 h-5 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 text-xs font-medium">{taskAssignedTo.avatar}</span>
                        Assigned to {taskAssignedTo.name}
                        <button
                          type="button"
                          onClick={() => setTaskAssignedTo(null)}
                          className="ml-0.5 text-orange-600 hover:text-orange-700 p-0.5 rounded"
                          aria-label="Remove assignment"
                        >
                          ×
                        </button>
                      </span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    {activeMode === 'team' && (
                      <label className="flex items-center space-x-2 text-gray-600">
                        <input
                          type="checkbox"
                          id="shareTask"
                          className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                        />
                        <span className="text-sm">Share with team</span>
                      </label>
                    )}
                    <button
                      onClick={() => {
                        onAddTask(taskAssignedTo ?? undefined);
                        setTaskAssignedTo(null);
                      }}
                      className="px-6 py-2.5 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-lg font-medium transition-colors text-sm"
                    >
                      Add Task
                    </button>
                  </div>
                </div>

                {/* Task List */}
                <div className="space-y-3 max-h-64 sm:max-h-80 overflow-y-auto">
                  {tasks.map((task) => (
                    <div key={task.id} className="flex items-center space-x-3 p-4 bg-white border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm transition-all">
                      <button
                        onClick={() => onToggleTask(task.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 text-xs ${task.completed
                          ? 'bg-orange-600 border-orange-600 text-white'
                          : 'border-gray-300 hover:border-orange-400'
                          }`}
                      >
                        {task.completed && '✓'}
                      </button>
                      <div className="flex-1 min-w-0">
                        <span className={`text-sm ${task.completed ? 'text-gray-500 line-through' : 'text-gray-900'} truncate block`}>
                          {task.title || task.text}
                        </span>
                        {task.description && (
                          <span className={`text-xs ${task.completed ? 'text-gray-400 line-through' : 'text-gray-500'} mt-0.5 truncate block`}>
                            {task.description}
                          </span>
                        )}
                        {(task.assignedTo || task.assigned_to) && (() => {
                          const assignee = task.assignedTo ||
                            (typeof task.assigned_to === 'object' && task.assigned_to !== null
                              ? { ...(task.assigned_to as any), avatar: '' }
                              : null);

                          if (!assignee) return null;

                          return (
                            <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-700">
                              {assignee.avatar ? (
                                <img src={assignee.avatar} alt={assignee.name} className="w-3.5 h-3.5 rounded-full object-cover" />
                              ) : (
                                <span className="w-3.5 h-3.5 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-medium" style={{ fontSize: '8px' }}>
                                  {assignee.name?.charAt(0).toUpperCase() || '?'}
                                </span>
                              )}
                              <span className="truncate max-w-[60px] text-[10px]">{assignee.name}</span>
                            </span>
                          );
                        })()}
                      </div>

                      {(() => {
                        const status = task.status || (task.completed ? 'done' : 'todo');
                        if (status === 'done') {
                          return <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide text-green-700 bg-green-100 border border-green-200/50 rounded-full uppercase flex-shrink-0">Done</span>;
                        } else if (status === 'inprogress') {
                          return <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide text-blue-700 bg-blue-100 border border-blue-200/50 rounded-full uppercase flex-shrink-0">In Progress</span>;
                        } else {
                          return <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide text-gray-700 bg-gray-100 border border-gray-200/50 rounded-full uppercase flex-shrink-0">To Do</span>;
                        }
                      })()}

                      <button
                        onClick={() => onEditTask(task.id)}
                        className="text-gray-400 hover:text-orange-600 text-sm p-1 flex-shrink-0 transition-colors"
                        title="Edit task"
                      >
                        <MdEdit className="text-base" />
                      </button>
                      <button
                        onClick={() => onDeleteTask(task.id)}
                        className="text-gray-400 hover:text-red-500 text-sm p-1 flex-shrink-0 transition-colors"
                        title="Delete task"
                      >
                        <RiDeleteBin4Fill className="text-base" />
                      </button>
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <div className="text-gray-500 text-center py-12 text-sm">No Tasks yet.</div>
                  )}
                </div>
              </div>
            )}

            {/* Reminders Tab */}
            {/* Reminders Tab - Always mounted to keep timer running */}
            <div style={{ display: sessionTab === 'reminders' ? 'block' : 'none' }}>
              <CheckIns
                reminders={reminders}
                setReminders={setReminders}
              />
            </div>

            {/* Teams Tab - Only for Team Mode */}
            {sessionTab === 'teams' && activeMode === 'team' && (
              <div>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                  <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                    Team Collaboration
                  </h3>
                  <div className="flex items-center space-x-2">
                    {teamMembers.slice(0, 4).map((member) => (
                      <button
                        key={member.id}
                        onClick={() => onSetShowMembersPopup(true)}
                        className="relative"
                      >
                        <div className="w-8 h-8 bg-orange-100 border border-orange-200/60 rounded-full flex items-center justify-center text-orange-600 font-medium text-xs">
                          {member.avatar}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border-2 border-white ${member.isOnline ? 'bg-orange-500' : 'bg-gray-400'}`}></div>
                        {member.isMuted && (
                          <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-orange-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-[8px]">🔇</span>
                          </div>
                        )}
                      </button>
                    ))}
                    {teamMembers.length > 4 && (
                      <button
                        onClick={() => onSetShowMembersPopup(true)}
                        className="w-8 h-8 bg-white border border-gray-200/60 rounded-full flex items-center justify-center text-gray-600 text-xs font-medium hover:border-orange-200/60 transition-colors"
                      >
                        +{teamMembers.length - 4}
                      </button>
                    )}
                  </div>
                </div>


                {/* Shared Tasks */}
                <div className="mb-8">
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-4">
                    Shared Tasks
                  </h4>
                  <div className="space-y-3 max-h-48 sm:max-h-60 overflow-y-auto">
                    {sharedTasks.map((task) => (
                      <div key={task.id} className="flex items-center space-x-3 p-4 bg-white border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm transition-all">
                        <button
                          onClick={() => onToggleSharedTask(task.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 text-xs ${task.completed
                            ? 'bg-orange-600 border-orange-600 text-white'
                            : 'border-gray-300 hover:border-orange-400'
                            }`}
                        >
                          {task.completed && '✓'}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm ${task.completed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                            {task.title || task.text}
                            {(task.assignedTo || task.assigned_to) && (() => {
                              const assignee = task.assignedTo ||
                                (typeof task.assigned_to === 'object' && task.assigned_to !== null
                                  ? { ...(task.assigned_to as any), avatar: '' }
                                  : null);

                              if (!assignee) return null;

                              return (
                                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 border border-orange-200/50 rounded-full text-xs text-orange-700">
                                  <span className="w-4 h-4 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-medium" style={{ fontSize: '10px' }}>
                                    {assignee.avatar ? (
                                      <img src={assignee.avatar} alt={assignee.name} className="w-4 h-4 rounded-full object-cover" />
                                    ) : (
                                      assignee.name?.charAt(0).toUpperCase() || '?'
                                    )}
                                  </span>
                                  {assignee.name}
                                </span>
                              );
                            })()}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            By {task.createdBy} • {task.createdAt.toLocaleTimeString()}
                          </div>
                        </div>
                        <button
                          onClick={() => onDeleteSharedTask(task.id)}
                          className="text-gray-400 hover:text-orange-600 text-sm flex-shrink-0 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    {sharedTasks.length === 0 && (
                      <div className="text-gray-500 text-center py-12 text-sm">No shared tasks yet.</div>
                    )}
                  </div>
                </div>

                {/* Shared Notes */}
                <div>
                  <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-4">
                    Shared Notes
                  </h4>
                  <div className="space-y-3 max-h-48 sm:max-h-60 overflow-y-auto">
                    {sharedNotes.map((note) => (
                      <div key={note.id} className="p-4 bg-white border border-gray-200/60 rounded-xl hover:border-orange-200/60 hover:shadow-sm transition-all cursor-pointer" onClick={() => {
                        onSetSelectedNote({ id: note.id, text: note.text, createdAt: note.createdAt });
                        onSetShowNoteModal(true);
                      }}>
                        <div className="text-gray-900 text-sm line-clamp-2">{note.text}</div>
                        <div className="text-xs text-gray-500 mt-2">
                          By {note.createdBy} • {note.createdAt.toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                    {sharedNotes.length === 0 && (
                      <div className="text-gray-500 text-center py-12 text-sm">No shared notes yet.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={10000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          containerId="session-toast"
        />
      </div>

      {/* Extend Session Modal */}
      {showExtendModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-[#FFF8F0] rounded-2xl p-6 sm:p-8 max-w-md w-full border border-gray-200/60 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Extend Session</h3>
              <button
                onClick={() => setShowExtendModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
              >
                <IoClose />
              </button>
            </div>

            <p className="text-gray-600 mb-6">How much time would you like to add?</p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {[5, 10, 15, 20, 30, 45].map((minutes) => (
                <button
                  key={minutes}
                  onClick={() => setExtendMinutes(minutes)}
                  className={`px-4 py-2.5 rounded-xl font-medium transition-all duration-300 text-sm ${extendMinutes === minutes
                    ? 'bg-white border-2 border-orange-600 text-orange-600'
                    : 'bg-white border border-gray-200 hover:border-orange-300 text-gray-700 hover:bg-orange-50/30'
                    }`}
                >
                  +{minutes}m
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowExtendModal(false)}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-200 hover:border-gray-300 text-gray-700 rounded-xl font-medium transition-all duration-300 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExtendSession(extendMinutes)}
                className="flex-1 px-4 py-2.5 bg-white border border-orange-600 hover:bg-orange-50 text-orange-600 rounded-xl font-medium transition-all duration-300 text-sm"
              >
                Add {extendMinutes} minutes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusSessionModal;

